import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../components/table/data-table";
import { InviteModal, SuccessModal } from "../../components/modal";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const POSITIONS_CACHE_KEY = "cehr_company_positions_v1";

const Position = () => {
  const cachedData = useMemo(
    () => readSessionCache(POSITIONS_CACHE_KEY, { positions: [] }),
    [],
  );
  const [openPositionModal, setOpenPositionModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [positions, setPositions] = useState(cachedData?.positions || []);
  const [positionName, setPositionName] = useState("");
  const [positionDescription, setPositionDescription] = useState("");
  const [isLoading, setIsLoading] = useState(!cachedData?.positions?.length);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const openModal = () => setOpenPositionModal(true);
  const apiBaseUrl = getApiBaseUrl();

  const loadPositions = useCallback(
    async ({ silent = false } = {}) => {
      if (!apiBaseUrl) {
        setErrorMessage("API base URL is not configured.");
        return;
      }

      try {
        if (!silent && positions.length === 0) {
          setIsLoading(true);
        }
        setErrorMessage("");

        const response = await axios.get(
          `${apiBaseUrl}/organizations/positions`,
        );
        const nextPositions = response?.data?.positions || [];
        setPositions(nextPositions);
        writeSessionCache(POSITIONS_CACHE_KEY, { positions: nextPositions });
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message ||
          "Unable to load positions. Please try again.";
        setErrorMessage(
          Array.isArray(responseMessage)
            ? responseMessage.join(", ")
            : responseMessage,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl, positions.length],
  );

  useEffect(() => {
    loadPositions({ silent: true });
  }, [loadPositions]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadPositions({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadPositions]);

  const resetForm = () => {
    setPositionName("");
    setPositionDescription("");
    setErrorMessage("");
  };

  const closePositionModal = () => {
    setOpenPositionModal(false);
    resetForm();
  };

  const createHandleSuccess = async (ev) => {
    ev.preventDefault();

    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    if (!positionName.trim() || !positionDescription.trim()) {
      setErrorMessage("Position name and position description are required.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      await axios.post(`${apiBaseUrl}/organizations/positions`, {
        positionName: positionName.trim(),
        positionDescription: positionDescription.trim(),
      });

      closePositionModal();
      setOpenSuccessModal(true);
      await loadPositions();
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to create position. Please try again.";
      setErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  let navigate = useNavigate();

  const headings = ["Position Name", "Position Description", "Actions"];
  const columns = ["positionName", "positionDescription"];

  return (
    <div className="dashboard-enter p-6">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Positions" },
        ]}
        title="Positions"
        subtitle="Manage position titles used in the organogram and approvals."
        fallbackPath="/company-settings"
        backLabel="Back to Company Settings"
        actions={
          <button
            onClick={openModal}
            className="bg-[#1234CD] px-6 py-2 rounded-xl text-white"
          >
            Add new
          </button>
        }
      />

      <DataTable
        title=""
        headings={headings}
        columns={columns}
        data={positions}
        showEmptyState={!isLoading}
        emptyMessage="No positions found. Create one to get started."
        onView={(row) => {
          sessionStorage.setItem("selectedPositionId", row.id);
          navigate("/company-settings/positionview-details", {
            state: { positionId: row.id },
          });
        }}
      />

      <InviteModal
        isOpen={openPositionModal}
        onClose={closePositionModal}
      >
        <div className="my-8">
          <div>
            <p className=" text-2xl font-semibold text-black font-Outfit">
              Create Position
            </p>
          </div>
          <form
            className="my-12"
            onSubmit={createHandleSuccess}
          >
            {errorMessage && (
              <p
                className="mb-4 text-sm text-red-600 dashboard-fade-in"
                role="alert"
              >
                {errorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="position-name"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Position Name
              </label>
              <input
                type="text"
                id="position-name"
                placeholder="Title"
                value={positionName}
                onChange={(ev) => setPositionName(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="position-description"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Position Description
              </label>

              <textarea
                id="position-description"
                placeholder="Description"
                value={positionDescription}
                onChange={(ev) => setPositionDescription(ev.target.value)}
                rows={4}
                wrap="soft"
                className="w-full p-2 min-h-36 mb-4 resize-y whitespace-pre-wrap break-words rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div className="flex justify-center mt-20 items-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="font-[700] font-satoshi leading-[21.6px] w-40 rounded-xl text-white py-[16px] bg-[#1234CD] h-[40px]text-[16px] disabled:opacity-60 disabled:cursor-not-allowed dashboard-button-hover"
              >
                <span>{isSubmitting ? "Creating..." : "Create"}</span>
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
      />

      {isLoading && positions.length === 0 ? (
        <FetchLoadingState message="Loading positions..." />
      ) : null}
    </div>
  );
};

export default Position;
